import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { useSafetyTranslation } from 'cng-common/features/translations'

import type { ICockpitExportPayload } from '../../api'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../configs/translations'
import { formatCockpitScore, toBadgeScore } from '../../lib'
import { useCockpitExportContext } from '../../model/hooks'
import { selectCockpitRevenueAndSobView } from '../../model/selectors'
import { CockpitPlanQualityTable } from '../cockpitPlanQuality/components'
import { useCockpitExportSlot, useRegisterCockpitExport } from '../components/cockpitExport'

import {
  CockpitFinSobQualityPillars,
  CockpitRevenueAndSobTable,
  TCockpitTableView,
} from './components'
import { useRevenueAndSobViewScores } from './hooks/useRevenueAndSobViewScores'

import styles from './CockpitRevenueAndSobExpanded.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS

const TILE_TITLE = 'Financial & SOB'

/** Names the view strip in an exported snapshot, as the Initiatives tile names its own. */
const VIEW_LABEL = 'View'

interface ICockpitRevenueAndSobExpandedProps {
  /** Restricts the tile's data to the manager cockpit's currently filtered partner list. Only
   *  meant for the manager view — the single-partner cockpit omits this. */
  isManagerCockpitMode?: boolean
}

/** The expanded tile's body. Its view switch stands in the tile header — see
 *  `CockpitRevenueAndSobTitle` — and both read the view from the slice. */
export const CockpitRevenueAndSobExpanded = ({
  isManagerCockpitMode = false,
}: ICockpitRevenueAndSobExpandedProps = {}) => {
  const translate = useSafetyTranslation()
  const exportSlot = useCockpitExportSlot()
  const exportContext = useCockpitExportContext(isManagerCockpitMode)
  const requestedView = useSelector(selectCockpitRevenueAndSobView)
  const selectedView: TCockpitTableView =
    requestedView === 'quality' ? 'planQuality' : 'revenueAndSob'

  const { achievementScore, planQualityScore, planQuality } =
    useRevenueAndSobViewScores(isManagerCockpitMode)

  /** The manager cockpit shows the cockpit's own Geo x Product x Partner table; the single-partner
   *  cockpit keeps the partner page's pillar cards, which link back into that partner's own
   *  planning. */
  const planQualityContent = isManagerCockpitMode ? (
    <CockpitPlanQualityTable showGeoHierarchy exportSlot={exportSlot} />
  ) : (
    <CockpitFinSobQualityPillars quality={planQuality} exportSlot={exportSlot} />
  )

  /** The strip as the switcher shows it: each tab wears its own score, and a pill always reads a
   *  number — `toBadgeScore` turns an absent score into its 0, exactly as the two badges do. */
  const tabLabel = (view: TCockpitTableView) =>
    view === 'revenueAndSob'
      ? `${translate(KEYS.revenueAndSob.tileTitle)}${
          achievementScore === undefined
            ? ''
            : ` · ${formatCockpitScore(toBadgeScore(achievementScore))}`
        }`
      : `${translate(KEYS.planQuality.title)}${
          planQualityScore === undefined
            ? ''
            : ` · ${formatCockpitScore(toBadgeScore(planQualityScore))}`
        }`

  /** What the tile shows right now — a table, or a single partner's Plan Quality pillar cards. */
  const buildExport = useCallback((): ICockpitExportPayload | null => {
    const view = exportSlot.current?.()

    if (!view) {
      return null
    }

    return {
      type: 'finSob',
      data: {
        title: TILE_TITLE,
        context: exportContext,
        view: {
          ...view,
          controls: [
            {
              label: VIEW_LABEL,
              value: tabLabel(selectedView),
              options: [tabLabel('revenueAndSob'), tabLabel('planQuality')],
            },
            ...view.controls,
          ],
        },
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportSlot, exportContext, selectedView, achievementScore, planQualityScore, translate])

  useRegisterCockpitExport(buildExport)

  return (
    <div className={styles.root}>
      {selectedView === 'revenueAndSob' ? (
        <CockpitRevenueAndSobTable
          showGeoHierarchy={isManagerCockpitMode}
          exportSlot={exportSlot}
        />
      ) : (
        planQualityContent
      )}
    </div>
  )
}
